import React, { FormEvent, ChangeEvent, KeyboardEvent, useRef, useState, useEffect } from "react";
import { User, Localizations, ObserverLocalizations, MentorLocalizations } from "../sharedTypes";
import { Chat as IChat, ChatType } from "../GeneratedTypes";
import { Box, Grid, Button, TextField, Select, InputLabel } from "@material-ui/core";
import ChatMessage from "./ChatMessage";
import { Send } from "@material-ui/icons";
import { Alert, AlertTitle } from "@material-ui/lab";
import { MentorUserInfo } from "../Auto/Interfaces/MentorUserInfo";
import { ChatTypeSelect } from "./ChatTypeSelect";
import { ParticipantType } from "../Auto/Interfaces/ParticipantType";

interface PropTypes {
    forwardedRef?: any;
    chats: IChat[];
    sessionActive: boolean;
    participantType: ParticipantType;
    canSeeChatTypes: ChatType[];
    canSendChatTypes: ChatTypeSelect[];
    addChat: (message: string, chatType: ChatTypeSelect) => void;
    setFullScreen?: (fullScreen: boolean) => void;
    clientLocalizations?: Localizations;
    observerLocalizations?: ObserverLocalizations;
    mentorLocalizations?: MentorLocalizations;
    userId?: string | string[];
    clientPaddingTop: number;
}

export default function ChatSystem(props: PropTypes) {
    let [chatInputVal, setChatInputVal] = useState(""),
        [chatTypeIndex, setChatTypeIndex] = useState(""),
        chatInput = useRef<HTMLDivElement>(null),
        chatScrollToViewRef = useRef<HTMLDivElement>(null),
        audio = useRef<HTMLAudioElement>();

    useEffect(() => {
        scrollChatsToBottom();
        if (!audio.current) {
            audio.current = new Audio("/sound/sound.wav");
        }
    }, [props.chats]);

    function addChat(text: string) {
        audio.current!.play();
        props.addChat(text, props.canSendChatTypes[parseInt(chatTypeIndex as string)]);
    }

    function clearChatInput() {
        setChatInputVal("");
    }
    function chatFormSubmitted(event: FormEvent) {
        event.preventDefault();
        event.stopPropagation();
        submitChat();
    }

    function chatInputChanged(event: ChangeEvent<HTMLInputElement>) {
        setChatInputVal(event.target.value);
    }

    function chatInputKeyDown(event: KeyboardEvent) {
        if (event.key === "Enter") {
            submitChat();
            event.preventDefault();
        }
    }

    function scrollChatsToBottom() {
        chatScrollToViewRef.current!.scrollIntoView({
            behavior: "smooth",
        });
    }

    function submitChat() {
        if (!chatInputVal.length || chatTypeIndex === null) {
            return;
        }

        addChat(chatInputVal);
        clearChatInput();

        chatInput!.current!.focus();
    }

    function changeChatTypeSelected(newChatTypeIndex: string) {
        setChatTypeIndex(newChatTypeIndex);
    }

    let chats = props.chats
        .map((c, i) => {
            return (
                <ChatMessage
                    key={i}
                    chat={c}
                    participantType={props.participantType}
                    isMine={isMyUserId(c.fromId) || isClientAndClientChat(c.authorType ?? null)}
                    clientLocalizations={props.clientLocalizations}
                    mentorLocalizations={props.mentorLocalizations}
                ></ChatMessage>
            );
        });

    function isMyUserId(fromId: string | undefined): boolean {
        if (typeof props.userId === "string") {
            return props.userId === fromId;
        }

        if (Array.isArray(props.userId)) {
            return !!props.userId.find(x => x === fromId);
        }

        return false;
    }

    function isClientAndClientChat(chatType: ParticipantType | null): boolean {
        return props.participantType === ParticipantType.Client &&
            chatType === ParticipantType.Client;
    }

    chatTypeIndex = chatTypeIndex || ChatType.Public.toString();

    var opts = props.canSendChatTypes.map((ct, i) => (
        <option key={i} value={i}>
            {ct.ToDisplay}
        </option>
    ));

    let showChatTypes = opts.length > 1;

    return (
        <>
            <Grid
                ref={props.forwardedRef}
                item
                container
                zeroMinWidth
                wrap="nowrap"
                direction="column"
                style={{ minHeight: 0, flex: 1, paddingTop: props.clientPaddingTop, paddingLeft: 10, paddingRight: 10, paddingBottom: 10 }}
            >
                <Box
                    p={2}
                    mb={2}
                    pb={4}
                    // itemRef={this.chatDisplay}
                    borderRadius={4}
                    style={{
                        flexGrow: 1,
                        overflowY: "auto",
                        overflowX: "hidden",
                        minHeight: 0,
                    }}
                    boxShadow={2}
                >
                    {chats}
                    <div ref={chatScrollToViewRef}></div>
                </Box>

                <form onSubmit={chatFormSubmitted} style={{ marginBottom: 80 }}>
                    {showChatTypes ?
                        <>
                            <InputLabel>{props.clientLocalizations?.chatTo ?? props.mentorLocalizations?.chatTo ?? "To"}</InputLabel>
                            <Select
                                fullWidth
                                value={chatTypeIndex}
                                onChange={(
                                    e: React.ChangeEvent<{
                                        value: unknown;
                                    }>
                                ) => changeChatTypeSelected(e.target.value as string)}
                                native
                            >
                                {opts}
                            </Select>
                        </>
                        : <></>}
                    <TextField
                        label={
                            props.clientLocalizations?.message ?? props.mentorLocalizations?.message ?? "Message"
                        }
                        id={(props.clientLocalizations?.yourMessageHere ??
                            props.mentorLocalizations?.yourMessageHere ??
                            "Your message here.").split(' ').join('')}
                        multiline
                        ref={chatInput}
                        variant="standard"
                        value={chatInputVal}
                        onChange={chatInputChanged}
                        rows="2"
                        // InputLabelProps={{
                        //     shrink: true
                        // }}
                        fullWidth
                        helperText={
                            props.clientLocalizations?.pressButtonOrEnterToSend ??
                            props.mentorLocalizations?.pressButtonOrEnterToSend ??
                            "Press button or enter to send."
                        }
                        onKeyDown={chatInputKeyDown}
                        placeholder={
                            props.clientLocalizations?.yourMessageHere ??
                            props.mentorLocalizations?.yourMessageHere ??
                            "Your message here."
                        }
                    ></TextField>
                    <Box textAlign="right">
                        <Button
                            startIcon={<Send />}
                            onClick={() => {
                                props.setFullScreen && props.setFullScreen(true);
                            }}
                            type="submit"
                            color="primary"
                            size="small"
                            variant="contained"
                        >
                            {props.clientLocalizations?.sendMessage ??
                                props.mentorLocalizations?.sendMessage ??
                                "Send Message"}
                        </Button>
                    </Box>
                </form>
            </Grid>
        </>
    );
}
